// SPDX-License-Identifier: AGPL-3.0-or-later

import { Monitor, Moon, Sun } from 'lucide-react';
import { Card } from '@intakeform/ui';
import { useMe } from '../../api/auth.js';
import { useTheme } from '../../theme/index.js';
import { formatRole } from './TeamRoute.js';
import { ChangePasswordCard } from './ChangePasswordCard.js';

export function UserProfileSettingsRoute() {
  const { data: me } = useMe();
  const { preference, setPreference } = useTheme();

  if (!me) return null;

  const initials = me.user.displayName
    ? me.user.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '??';

  return (
    <div className="space-y-8">
      {/* User Profile Card */}
      <Card heading="Clinician Profile" variant="default">
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '12px 0' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 'var(--radius-full)',
              background: 'rgb(var(--color-primary-subtle))',
              color: 'rgb(var(--color-primary-foreground))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'var(--weight-semibold)',
              fontSize: 'var(--text-lg)',
              border: '2px solid rgb(var(--color-primary))',
            }}
          >
            {initials}
          </div>
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--weight-semibold)',
              }}
            >
              {me.user.displayName}
            </h3>
            <p className="text-sm text-muted-foreground" style={{ margin: '2px 0 0 0' }}>
              {me.user.email}
            </p>
          </div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 16,
            borderTop: '1px solid rgb(var(--color-border))',
            marginTop: 16,
            paddingTop: 16,
          }}
        >
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              Role
            </span>
            <span className="text-sm font-medium mt-1 block">{formatRole(me.user.role)}</span>
          </div>
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              Organization
            </span>
            <span className="text-sm font-medium mt-1 block">{me.organization.name}</span>
          </div>
          <div>
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block">
              Joined
            </span>
            <span className="text-sm font-medium mt-1 block">
              {new Date(me.user.createdAt).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>
      </Card>

      {/* Theme Selector Card */}
      <Card heading="Theme Preferences" variant="default">
        <p className="text-sm text-muted-foreground mb-4">
          Choose a visual theme for the application interface.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12,
          }}
        >
          {(
            [
              {
                id: 'light',
                label: 'Light Mode',
                Icon: Sun,
                desc: 'Clean, high-contrast light theme',
              },
              { id: 'dark', label: 'Dark Mode', Icon: Moon, desc: 'Easy on the eyes in low light' },
              {
                id: 'system',
                label: 'System Default',
                Icon: Monitor,
                desc: 'Syncs with OS theme settings',
              },
            ] as const
          ).map((item) => {
            const active = preference === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setPreference(item.id)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  padding: 16,
                  borderRadius: 'var(--radius-md)',
                  background: active
                    ? 'rgb(var(--color-surface-raised))'
                    : 'rgb(var(--color-surface))',
                  border: active
                    ? '2px solid rgb(var(--color-primary))'
                    : '1px solid rgb(var(--color-border))',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  outline: 'none',
                }}
                className={active ? 'theme-option-active' : 'theme-option-inactive'}
              >
                <item.Icon
                  size={24}
                  style={{
                    color: active
                      ? 'rgb(var(--color-primary))'
                      : 'rgb(var(--color-muted-foreground))',
                    marginBottom: 8,
                  }}
                />
                <span
                  style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: active ? 'var(--weight-semibold)' : 'var(--weight-normal)',
                    color: 'rgb(var(--color-foreground))',
                  }}
                >
                  {item.label}
                </span>
                <span
                  className="text-xs text-muted-foreground"
                  style={{ marginTop: 4, display: 'block', maxWidth: 140 }}
                >
                  {item.desc}
                </span>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Change Password Card */}
      <ChangePasswordCard />
    </div>
  );
}
